import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { TranslateService } from '@ngx-translate/core';

export interface ErrorLog {
  success: boolean;
  code: string | number;
  message: string; // Translation key
  translatedMessage?: string;
  timestamp: number;
}

export interface ErrorState {
  errors: ErrorLog[];
  warnings: ErrorLog[];
  maxErrors: number;
}

export const ErrorStore = signalStore(
  { providedIn: 'root' },
  withDevtools('errors'),
  withState<ErrorState>({
    errors: [],
    warnings: [],
    maxErrors: 10, // Maximum number of errors to keep in history
  }),

  // Computed properties
  withComputed((store) => ({
    latestError: computed(() => (store.errors().length > 0 ? store.errors()[0] : null)),
    errorCount: computed(() => store.errors().length),

    errorsByCode: computed(() => {
      const grouped: Record<string | number, ErrorLog[]> = {};
      store.errors().forEach((error) => {
        const code = error.code.toString();
        if (!grouped[code]) {
          grouped[code] = [];
        }
        grouped[code].push(error);
      });
      return grouped;
    }),
  })),

  withMethods((store, translateService = inject(TranslateService)) => ({
    /*
     * Log an error to the store only if the message is translatable
     */
    logError(errorData: Omit<ErrorLog, 'timestamp' | 'translatedMessage'>, isWarn = false): void {
      const translatedMessage = translateService.instant(errorData.message);
      const hasTranslation = translatedMessage !== errorData.message;

      if (hasTranslation) {
        const newError: ErrorLog = {
          ...errorData,
          translatedMessage,
          timestamp: Date.now(),
        };

        // TODO: Come back and clean this up for optional modal usage. We don't want to always auto-show this modal
        if (!isWarn) {
          // Get current errors and add the new one at the beginning
          const updatedErrors = [newError, ...store.errors()];

          // Keep only up to maxErrors
          if (updatedErrors.length > store.maxErrors()) {
            updatedErrors.length = store.maxErrors();
          }

          patchState(store, { errors: updatedErrors });
        } else {
          // Get current errors and add the new one at the beginning
          const updatedWarnings = [newError, ...store.errors()];

          // Keep only up to maxErrors
          if (updatedWarnings.length > store.maxErrors()) {
            updatedWarnings.length = store.maxErrors();
          }

          patchState(store, { warnings: updatedWarnings });
        }
      }
    },

    clearErrors(): void {
      patchState(store, { errors: [], warnings: [] });
    },

    // Force retranslate all error messages (useful when language changes)
    retranslateMessages(): void {
      const updatedErrors = store.errors().map((error) => ({
        ...error,
        translatedMessage: translateService.instant(error.message),
      }));

      const updatedWarnings = store.warnings().map((error) => ({
        ...error,
        translatedMessage: translateService.instant(error.message),
      }));

      patchState(store, { errors: updatedErrors, warnings: updatedWarnings });
    },
  })),
);
